import {
  Input,
  Icon,
  DateRangeSelect,
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TableCaption,
  SkeletonLoader,
  Pagination,
  Badge
} from '@awell-health/design-system'

import { type RouteComponentProps } from '@reach/router'
import React, { useEffect, useState, useCallback, type FC } from 'react'
import { t } from 'i18next'
import { useGetOrchestrationFacts, type OrchestrationFact } from '../../../hooks/useGetOrchestrationFacts'
import { usePathwayContext } from '../../../hooks/usePathwayContext'
import { FactContentCell } from './FactContentCell'
import { useDebouncedCallback } from 'use-debounce'
import { isNil } from 'lodash'
import { endOfDay, format, parseISO, startOfDay } from 'date-fns'

interface OrchestrationFactFilters {
  startDate: string | undefined
  endDate: string | undefined
  query: string
}

export const AuditLogs: FC<RouteComponentProps> = ({ location }) => {
  const { pathwayId } = usePathwayContext()

  const queryParams = new URLSearchParams(location?.search)
  const toDateQueryParam = queryParams.get('to')

  const { loading, refetchOrchestrationFacts } = useGetOrchestrationFacts(pathwayId!)

  const [appliedFilters, setFilters] = useState<OrchestrationFactFilters>({
    startDate: undefined,
    endDate: undefined,
    query: ''
  })

  // Pagination state for backend pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [rows, setRows] = useState<OrchestrationFact[]>([])
  const [sortDirection, setSortDirection] = useState('desc')
  const [isLoading, setIsLoading] = useState(false)
  const itemsPerPage = 10

  useEffect(() => {
    void debouncedDataFetch()
    setCurrentPage(1) // Reset to first page when filters change
  }, [appliedFilters])

  useEffect(() => {
    if (!isNil(toDateQueryParam) && toDateQueryParam !== '') {
      setFilters({
        ...appliedFilters,
        endDate: toDateQueryParam
      })
      setSortDirection('desc')
    }
  }, [toDateQueryParam])

  const debouncedDataFetch = useDebouncedCallback(() => fetchData(), 1000)

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value
    setFilters(prev => ({
      ...prev,
      query
    }))
  }, [])

  const handleDateRangeSelect = useCallback(
    (from: string, to: string): void => {
      const startDate = from || undefined
      const endDate = to || undefined

      // Prevent unnecessary updates if values haven't changed
      if (appliedFilters.startDate === startDate && appliedFilters.endDate === endDate) {
        return
      }

      setFilters(prev => ({
        ...prev,
        startDate,
        endDate
      }))
    },
    [appliedFilters.startDate, appliedFilters.endDate]
  )

  const dateRange =
    appliedFilters.startDate || appliedFilters.endDate || toDateQueryParam
      ? {
          from: appliedFilters.startDate || '',
          to: appliedFilters.endDate || toDateQueryParam || ''
        }
      : undefined

  const columns = [
    { title: 'Timestamp', field: 'timestamp', width: 'w-60' },
    { title: 'Level', field: 'level', width: 'w-40' },
    { title: 'Log Details', field: 'content' }
  ]

  const fetchData = async () => {
    if (!pathwayId) return

    setIsLoading(true)
    const offset = (currentPage - 1) * itemsPerPage
    try {
      const {
        data: {
          pathwayFacts: { facts, pagination }
        }
      } = await refetchOrchestrationFacts({
        pagination: {
          count: itemsPerPage,
          offset
        },
        filters: {
          date: {
            lte: appliedFilters.endDate ? endOfDay(parseISO(appliedFilters.endDate)).toISOString() : undefined,
            gte: appliedFilters.startDate ? startOfDay(parseISO(appliedFilters.startDate)).toISOString() : undefined
          },
          pathway_id: pathwayId,
          keyword: appliedFilters.query
        },
        sorting: {
          field: 'date',
          direction: sortDirection as 'asc' | 'desc'
        }
      })

      setRows(facts)
      setTotalCount(pagination?.total_count ?? 0)
    } catch (error) {
      console.error('Error fetching audit logs:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    void fetchData()
  }, [currentPage, sortDirection, appliedFilters, pathwayId])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const getBadgeVariant = (level: string) => {
    switch (level) {
      case 'info':
        return 'primary'
      case 'warning':
        return 'warning'
      case 'error':
        return 'error'
      default:
        return 'default'
    }
  }

  return (
    <div>
      <div className="flex items-center pb-4 gap-4">
        <Input
          placeholder={t('audit_logs_search_placeholder')}
          prefixIcon={<Icon icon="RiSearchLine" />}
          value={appliedFilters.query}
          onChange={handleSearchChange}
        />
        <DateRangeSelect onSelect={handleDateRangeSelect} {...(dateRange && { defaultDateRange: dateRange })} />
      </div>
      <div>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map(({ field, title, width }) => (
                <TableHead key={field} sortable={false} className={width ? width : ''}>
                  {title}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {(loading || isLoading) &&
              [...Array(itemsPerPage).keys()].map(i => (
                <TableRow key={i}>
                  {columns.map(({ field }) => (
                    <TableCell key={field}>
                      <SkeletonLoader className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            {!(loading || isLoading) &&
              rows.map((fact, index) => (
                <TableRow key={`${fact.date}-${index}`}>
                  <TableCell className="min-w-60 font-normal">
                    {format(new Date(fact.date), 'dd MMM yyyy, h:mm:ss a')}
                  </TableCell>
                  <TableCell>
                    <Badge variant={getBadgeVariant(fact.level)} size={null}>
                      {fact.level}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-normal">
                    <FactContentCell facts={fact.content} />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          {!(loading || isLoading) && totalCount > itemsPerPage && (
            <TableCaption className="py-0">
              <Pagination
                totalCount={totalCount}
                onPageChange={handlePageChange}
                page={currentPage}
                perPage={itemsPerPage}
              />
            </TableCaption>
          )}
        </Table>
      </div>
    </div>
  )
}

AuditLogs.displayName = 'AuditLogs'
